<template>
  <div
    class="field-popup"
    :data-id="id"
    :id="`field-popup-${id}`"
    v-if="id"
    tabindex="-1"
    ref="fieldPopup"
    @keypress.enter="confirmField"
    @keypress.tab="tabPress"
    @keypress.esc="closeFieldPopup">
    <h2 class="field-popup__header">
      <span class="field-popup__header__text" v-if="currentField.type !== 'dropdown'">{{ currentField.title === $t('fieldPopup.untitled') ? $t('fieldPopup.addYourField', { type: $t(`fieldPopup.type.${currentField.type}`) }) : currentField.title }}</span>
      <span class="field-popup__header__text" v-else>{{ $t('fieldPopup.selectAnOption') }}</span>
      <span v-if="fieldRequired(currentField)" class="field-popup__header__label">
        <span>
          (
          <icon  class="field-popup__header__label__asterisk" name="asterisk" height="10" width="10"></icon>
          {{ $t('fieldPopup.requiredField') }}
          )
        </span>
      </span>
      <span v-else class="field-popup__header__label">
        <span>{{ $t('fieldPopup.optional') }}</span>
      </span>
      <span v-if="currentField.required === 'true' && fieldCompleted" class="field-popup__header__label field-popup__header__label--complete">
        <icon class="field-popup__header__label--complete__icon" name="check" height="10" width="10" />
        <span>{{ $t('fieldPopup.complete') }}</span>
      </span>
      <a class="field-popup__close" @click="closeFieldPopup">
        <icon name="close" width="16" height="16"></icon>
      </a>
    </h2>

    <!-- Signature -->
    <div class="field-popup__content" v-if="currentField.type == 'signature'">
      <tabs type="signature" v-if="currentField">
        <tab :name="$t('fieldPopup.signature.draw')" property="drawn_json" id="1" iconName="pencil" v-if="$store.getters.config('company_signature_format_drawn')=== '1'">
          <signature-box
            :currentField="currentField"
            type="draw"
            :key="`sig-box-draw-${id}`"></signature-box>
        </tab>
        <tab :name="$t('fieldPopup.signature.type')" property="content" id="2" iconName="font" :hasInput="true" v-if="$store.getters.config('company_signature_format_typed') === '1'">
          <signature-box
            :currentField="currentField"
            type="type"
            :key="`sig-box-type-${id}`"></signature-box>
        </tab>
        <tab :name="$t('fieldPopup.signature.upload')" property="upload" id="3" iconName="cloud-upload" v-if="$store.getters.config('company_signature_format_upload') === '1'">
          <fine-uploader
            :key="`signature-uploader-${id}`"
            :field-id="id"
            :options="signatureUploadOpts"
            type="signature"
          />
        </tab>
      </tabs>
    </div>

    <!-- Text -->
    <div class="field-popup__content" v-if="currentField.type == 'text'">
      <input
        class="field-popup__field-text"
        :name="currentField.title !== 'Untitled' ? currentField.title : currentField.id"
        type="text" data-cy="submit-text-input"
        @input="updateFieldText"
        ref='input' />
    </div>

    <!-- Textarea -->
    <div class="field-popup__content" v-if="currentField.type == 'textarea'">
      <textarea class="field-popup__field-textarea" @input="updateFieldText" ref='input'></textarea>
    </div>

    <!-- Dropdown -->
    <div class="field-popup__content field-popup__content--dropdown" v-if="currentField.type == 'dropdown'">
      <div
        v-for="choice in currentField.dropdown_choices"
        @click.self="selectDropdownChoice"
        :key="choice"
        :class="{ selected: currentField.content === choice }">{{ choice }}</div>
    </div>

    <!-- Date -->
    <div class="field-popup__content" v-if="currentField.type == 'date'">
      <div class="flexGrid">
        <div class="flexGrid__item flexGrid__item--6 field-popup__content--datepicker" :key="`field-date-${id}`">
          <datepicker
            v-model="currentField.content"
            :inline="true"
            :field-id="id"
            @input="updateFieldValue"
            :language="$i18n.locale"></datepicker>
        </div>
        <div class="flexGrid__item flexGrid__item--6 field-popup__selected-date items-center">
          <h1 class="field-popup__date-text">
            <span v-if="currentField.content">{{ currentField.content }}</span>
            <span v-else>---</span>
          </h1>
          <BaseButton
            @click.native="clearContent"
            class="w-1/3 mb-base"
            :disabled="!currentField.content"
            variant="secondary"
            data-cy="clearDate"
          >
            {{ $t('functional.clear') }}
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- Upload -->
    <div class="field-popup__content" v-if="currentField.type == 'upload'">
      <fine-uploader
        :field-id="id"
        :options="documentUploadOpts"
        :key="`file-uploader-${id}`"
        type="document"
      />
    </div>

    <!-- Checkbox -->
    <div class="field-popup__content" v-if="currentField.type == 'checkbox' && !fieldCompleted">
      {{ $t('fieldPopup.tickABox') }}
    </div>
  </div>
</template>

<script>

import BaseButton from '@/components/library/buttons/BaseButton'
import SignatureBox from '@/components/SignatureBox'
import FineUploader from '@/components/FineUploader'
import Tabs from '@/components/Tabs'
import Tab from '@/components/Tab'
import Datepicker from '@/components/DatePicker'
import axios from 'axios'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'field-popup',
  props: {
    confirmField: {
      default: () => {},
      type: Function
    }
  },
  components: {
    BaseButton,
    SignatureBox,
    FineUploader,
    Tabs,
    Tab,
    Datepicker
  },
  data () {
    const customHeaders = {
      Authorization: axios.defaults.headers.common['Authorization']
    }

    return {
      config: this.$store.state.config,
      pickedDate: null,
      documentUploadOpts: {
        options: {
          request: {
            customHeaders,
            endpoint: `${process.env.VUE_APP_API_URL}/upload/file`
          },
          deleteFile: {
            customHeaders: {
              ...customHeaders,
              'X-HTTP-METHOD-OVERRIDE': 'POST'
            },
            endpoint: `${process.env.VUE_APP_API_URL}/upload/file`,
            enabled: true,
            method: 'POST'
          },
          validation: {
            allowedExtensions: ['jpeg', 'jpg', 'png', 'pdf', 'doc', 'docx', 'bmp'],
            acceptFiles: 'image/jpeg, image/png, application/pdf, application/x-pdf, application/msword, application/doc, application/vnd.msword, application/vnd.ms-word, application/winword, application/word, application/x-msw6, application/x-msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/bmp',
            sizeLimit: 5000000
          },
          multiple: true
        }
      },
      signatureUploadOpts: {
        options: {
          request: {
            customHeaders,
            endpoint: `${process.env.VUE_APP_API_URL}/upload/signature`
          },
          multiple: false,
          validation: {
            allowedExtensions: ['jpeg', 'jpg', 'png'],
            acceptFiles: 'image/jpeg, image/png',
            sizeLimit: 5000000,
            itemLimit: 1
          }
        }
      }
    }
  },
  updated () {
    if (this.currentField) {
      const fieldType = this.currentField.type
      if (fieldType === 'text' || fieldType === 'textarea') {
        this.$refs.input.focus()
        this.$refs.input.value = this.currentField.content
      } else {
        this.$refs.fieldPopup.focus()
      }
    }
  },
  computed: {
    ...mapGetters(['fieldRequired', 'fieldDone', 'getField']),
    id () {
      return this.$store.state.activeFieldId
    },
    currentField () {
      return this.getField(this.id)
    },
    fieldCompleted () {
      return this.fieldDone(this.currentField)
    }
  },
  methods: {
    ...mapActions(['setActiveField', 'setCurrentFieldVal']),
    updateFieldText (event) {
      this.setCurrentFieldVal({
        id: this.currentField.id,
        val: event.target.value,
        property: 'content'
      })
    },
    updateFieldValue (val, property = 'content') {
      this.setCurrentFieldVal({
        id: this.currentField.id,
        val,
        property
      })
    },
    selectDropdownChoice (event) {
      const value = event.target.innerText

      this.$store.dispatch('setCurrentFieldVal', {
        id: this.currentField.id,
        val: value,
        property: 'content'
      })
    },
    closeFieldPopup () {
      this.setActiveField(null)
    },
    clearContent () {
      this.currentField.content = null
    }
  }
}
</script>

<style lang="sass" scoped>
  @import '@/styles/component-utils.sass'
  $color-popup-shadow: $color-grey
  .field-popup
    background-color: #fff
    position: fixed
    bottom: 75px
    right: 0
    width: 75%
    z-index: 10
    transition: bottom .25s ease-out
    box-shadow: 0px -20px 30px -30px $color-popup-shadow
    cursor: default
    z-index: 999
    opacity: 0
    transition: all .25s ease
    color: #555
    opacity: 1
    &__header
      padding: $spacing-base
      border-bottom: 1px solid $color-border
      margin: 0
      position: relative
      @include clearfix
      &__text
        float: left
        white-space: nowrap
        text-overflow: ellipsis
        max-width: calc(100% - 150px)
        overflow: hidden
      &__label
        float: left
        font-size: 14px
        padding: $spacing-xs
        border-radius: 3px
        margin-left: $spacing-small
        &__asterisk
          color: white
          background-color: $color-error
          height: 10px
          width: 10px
          border-radius: 5px
          margin-right: 5px
        &--complete
          background-color: #65C87A
          color: #fff
          &__icon
            margin-right: $spacing-xs
    &__content
      padding: $spacing-base $spacing-base 0 $spacing-base
      &--dropdown
        padding: 0
        background: #FAFBFB
        border-bottom: 1px solid #ecf0f1
        margin-bottom: $spacing-base
        box-shadow: inset 0px 0px 7px 0 #eee
        max-height: 165px
        overflow: auto
        > div
          padding: $spacing-base
          font-size: 20px
          &:hover,
          &.selected
            cursor: pointer
          &:hover
            background: $signable-secondary-1-shade-3
          &.selected
            color: #fff
            background: $signable-secondary-1
      &--datepicker
        display: flex
        flex-direction: row
    &__buttons
      padding: 0 $spacing-base $spacing-base
      margin-top: $spacing-base
      @include clearfix
    &__field-text,
    &__field-textarea,
    &__field-dropdown
      width: 100%
      padding: $spacing-base
      border: 1px solid $color-border-dark
      font-size: $font-size-heading
      margin-bottom: $spacing-base
    &__field-textarea
      height: 250px
    &__field-dropdown
      padding: $spacing-base
      -webkit-appearance: none
      color: inherit
      font-family: inherit
    &__close
      position: absolute
      right: $spacing-base
      top: 12px
      padding: 7px
      background: $color-cancel
      border: 2px solid $color-cancel-dark
      color: #fff
      border-radius: 30px
      line-height: 0
      cursor: pointer
      svg
        margin-right: 0
    &__selected-date
      display: flex
      flex-direction: column
      justify-content: center
      gap: $spacing-small
    &__date-text
      margin: 0 auto
      padding: $spacing-base
      background-color: $color-blue
      color: #fff
      border-radius: 3px
      width: fit-content
    &__clear-button
      margin: 0 auto
      height: fit-content
      display: inline-flex
      float: none
      display: block
      width: fit-content
  // Responsive
  @media screen and (max-width: $screen-size-large)
    .field-popup
      width: 100%
      &__date-text
        position: initial
        right: 0
        transform: none
        text-align: center
  @media screen and (max-width: $screen-size-small)
    .field-popup
      &__date-text
        margin-top: $spacing-small
</style>
